import { Loader2 } from "lucide-react";
import { usePrintBridge } from "@/hooks/usePrintBridge";
import { bridgeDownloadUrl } from "@/lib/api";

/**
 * Compact live pill: "Label Printer Connected" (green) / "Not Connected" (red)
 * / "Checking…" (neutral). Ties into the shared usePrintBridge() hook so all
 * instances on the page share one ping, and status is consistent app-wide.
 *
 * Props:
 *   showSetup  — when disconnected, also render a "Download setup kit" link (default true)
 *   compact    — smaller pill for row-level placement (default false)
 *   testId     — override data-testid
 */
const LabelPrinterStatus = ({ showSetup = true, compact = false, testId = "label-printer-status" }) => {
  const { bridgeAlive } = usePrintBridge();

  const state =
    bridgeAlive === true ? "connected" :
    bridgeAlive === false ? "disconnected" : "checking";

  const dotCls =
    state === "connected"    ? "bg-emerald-500" :
    state === "disconnected" ? "bg-red-500"     :
                               "bg-slate-300 animate-pulse";

  const textCls =
    state === "connected"    ? "text-emerald-800" :
    state === "disconnected" ? "text-red-800"     :
                               "text-slate-500";

  const label =
    state === "connected"    ? "LAN Printer Online" :
    state === "disconnected" ? "LAN Printer Offline" :
                               "Checking LAN Printer…";

  const sizeCls = compact
    ? "text-[10px] tracking-wide font-semibold gap-1 px-1.5 py-0.5"
    : "text-[11px] font-semibold gap-1.5 px-2 py-1";

  return (
    <span
      data-testid={testId}
      data-state={state}
      className={`inline-flex items-center rounded-sm border ${sizeCls} ${
        state === "connected"    ? "bg-emerald-50 border-emerald-200" :
        state === "disconnected" ? "bg-red-50 border-red-200"         :
                                   "bg-slate-50 border-slate-200"
      } ${textCls}`}
    >
      {state === "checking" ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <span className={`inline-block h-2 w-2 rounded-full ${dotCls}`} />
      )}
      {label}
      {showSetup && state === "disconnected" && !compact && (
        <a
          href={bridgeDownloadUrl()}
          className="ml-1 underline decoration-red-400 hover:decoration-red-700"
          data-testid="lps-download-link"
          title="One-time setup — unzip and double-click Install_Autostart.bat"
        >
          Setup
        </a>
      )}
    </span>
  );
};

export default LabelPrinterStatus;
